<template>
  <div v-if="renderError" class="flex min-h-dvh flex-col items-center justify-center gap-4 p-6">
    <p class="text-[56px] leading-none" aria-hidden="true">⚠️</p>
    <p class="text-center text-[15px] text-text-muted">{{ renderError.message }}</p>
    <BaseButton v-if="homePath" variant="secondary" class="mt-2" @click="goHome">HOME</BaseButton>
  </div>
  <RouterView v-else />

  <AppSnackbar />
  <AppConfirmDialog />
</template>

<script setup lang="ts">
import { computed, onErrorCaptured, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useTheme } from '@/core/theme/useTheme'
import { useLiff } from '@/shared/composables/useLiff'
import AppConfirmDialog from '@/shared/ui/app-confirm-dialog.vue'
import AppSnackbar from '@/shared/ui/app-snackbar.vue'
import BaseButton from '@/shared/ui/base-button.vue'

const route = useRoute()

const { applyTheme } = useTheme()
const { init: initLiff } = useLiff()

const renderError = ref<Error | null>(null)

/**
 * 매장을 알 수 있을 때만 HOME 버튼을 보여준다.
 * 라이트 주문(POS 발행)에서는 돌아갈 매장 홈이 없으므로 숨긴다 (원본 동일).
 */
const homePath = computed(() => {
  const companyId = String(route.params.companyId ?? '').toLowerCase()
  const storeCode = String(route.params.storeCode ?? '')
  if (!companyId || !storeCode) return null
  if (route.path.includes('lite-order')) return null
  return `/${companyId}/${storeCode}`
})

// 다른 화면으로 이동하면 에러 상태를 푼다
watch(
  () => route.fullPath,
  () => {
    renderError.value = null
  },
)

onMounted(() => {
  // 매장 로드 전 기본 테마. 로드 후 applyShopTheme(shopData.theme)로 전환된다.
  // html[lang]은 initLocale()이 이미 설정했다 (폰트 분기용).
  applyTheme('default')

  // LINE 인앱일 때만 실제로 동작한다. 실패해도 일반 주문 흐름은 막지 않는다.
  void initLiff()
})

// 원본 ErrorBoundary 대응 — 렌더 예외를 전체 화면 에러로 승격
onErrorCaptured((error) => {
  renderError.value = error as Error
  return false
})

function goHome() {
  if (homePath.value) window.location.href = homePath.value
}
</script>
